import React, {useEffect, useState} from "react";
import SubHeader from "../../components/header/SubHeader";
import {useDispatch, useSelector} from "react-redux";
import {fetchCustomerOrder, updateCustomerPaymentStatus} from "../../store/customer-slice";
import {useParams} from "react-router-dom";
import Card from "../../components/ui/card/Card";
import {OrderStatus, PaymentStatus, PaymentType} from "../../enums/enums";
import {camelToTitle, getValidatedLimit, reverseEnum, toCamelCase} from "../../components/Utility";
import FilterOrders from "../../components/order/FilterOrders";
import Button from "../../components/common/button/Button";
import moment from "moment-timezone";
import {resetUpdatePaymentStatus} from "../../store/order-slice";
import {filterPresets} from "../../components/Utility";
import ConfirmModal from "../../components/common/modal/ConfirmModal";
import ActionButton from "../../components/actions/ActionButton";

const CustomerOrder = () => {
    const params = useParams();
    const dispatch = useDispatch();
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState("Cash");
    const [modalOpen, setModalOpen] = useState(false);
    const {pagination} = useSelector(state => state.customer);
    const {limit, currentPage} = getValidatedLimit(pagination);

    const userRole = useSelector((state) => state.auth.selectedRole);
    const timezone = 'Asia/Kolkata';

    const {
        customerItems: items,
        customer,
        updatePaymentStatus,
        fetchCustomerOrderStatus,
        error
    } = useSelector((state) => state.customer);

    const selectableIds = items
        .filter(item => item.paymentStatus !== PaymentStatus.Paid && item.paymentStatus !== PaymentStatus.Failed)
        .map(item => item._id);

    const isAllSelected =
        selectableIds.length > 0 &&
        selectableIds.every(id => selectedOrders.some(order => order.orderId === id));

// Handle select all checkbox
    const handleSelectAll = (e) => {
        if (isAllSelected) {
            // Remove all orders whose orderId is in selectableIds
            setSelectedOrders(prev =>
                prev.filter(order => !selectableIds.includes(order.orderId))
            );
        } else {
            // Add all selectableIds as { orderId, amount }
            const newOrders = selectableIds
                .map(id => {
                    const item = items.find(it => it._id === id); // assuming `items` holds your orders
                    return {orderId: id, amount: item?.totalPrice || 0};
                })
                .filter(Boolean); // remove null/undefined if item not found

            // Merge without duplicating existing ones
            setSelectedOrders(prev => {
                const existingIds = prev.map(o => o.orderId);
                const merged = [
                    ...prev,
                    ...newOrders.filter(o => !existingIds.includes(o.orderId))
                ];
                return merged;
            });
        }
    };

// Handle single checkbox click
    const handleSelectedOrders = (e, id, price) => {
        if (e.target.checked) {
            setSelectedOrders(prev => [...prev, {orderId: id, amount: price}]);
        } else {
            setSelectedOrders(prev => prev.filter(order => order.orderId !== id));
        }
    };

    //To show the sum of selected orders
    const totalSelectedPrice = selectedOrders.reduce(
        (sum, order) => sum + (order.amount || 0),
        0
    );

    //Clear due on confirm dialog
    const confirmHandler = () => {
        const updatedData = {
            selectOrders: selectedOrders,
            paymentMethod: paymentMethod
        }
        dispatch(updateCustomerPaymentStatus({
            ...userRole,
            customer: params.customerId,
            data: updatedData
        }));
        setModalOpen(false);
        setSelectedOrders([]);
    }

    //set modal to true
    const handleSettleSelectedDues = () => {
        setModalOpen(true); // open the modal
    };

    useEffect(() => {
        const queryString = new URLSearchParams({paymentStatus: PaymentStatus.Pending}).toString();
        dispatch(fetchCustomerOrder({...userRole, customerId: params.customerId, queryString}));
    }, [dispatch, userRole, params.customerId, updatePaymentStatus]);

    useEffect(() => {
        if (updatePaymentStatus === "success") {
            dispatch(resetUpdatePaymentStatus());
        }
    }, [dispatch, updatePaymentStatus])

    return (
        <>
            <SubHeader title="Customer Orders">
                <ActionButton
                    permissions={['BranchAdmin', 'BranchOwner', 'BranchManager']}
                    to={`/customer/${params.customerId}/customerTransaction/${toCamelCase(
                        `${customer?.fName || ""}${customer?.lName ? ` ${customer.lName}` : ""}`
                    )}`}
                    className="btn-sm bg-primary bg-opacity-50">View Transactions</ActionButton>
                <Button to={`../../?page=${currentPage}&limit=${limit}`}
                        className="btn-sm bg-secondary bg-opacity-50">Back</Button>

            </SubHeader>
            {fetchCustomerOrderStatus === "loading" && <p>Loading...</p>}
            {fetchCustomerOrderStatus === "failed" && <h6 className="text-danger">{error}</h6>}
            {fetchCustomerOrderStatus === "success" &&
                <>
                    <Card className="bg-transparent rounded-3 mb-4 shadow-sm">
                        <div className="row row-gap-2">
                            <div className="col-lg-6">
                                <h5 className="fw-semibold text-secondary text-capitalize">{customer?.fName + (customer?.lName ? ` ${customer.lName}` : '')}</h5>
                                <div className="d-flex justify-content-evenly">
                                    <div className="text-center">
                                        <h4 className="fs-4 mb-0 text-secondary">{customer.totalOrders}</h4>
                                        <h6 className="mb-0 text-primary text-body">Total orders</h6>
                                    </div>
                                    <div className="text-center">
                                        <h4 className="fs-4 mb-0 text-secondary"><span
                                            className="fw-semibold fs-md">&#8377; </span>{customer.totalFinalPrice.toFixed(2)}<span>/-</span>
                                        </h4>
                                        <h6 className="mb-0">Total Amount</h6>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-6">
                                <FilterOrders visibleFilters={filterPresets.customerOrderFilter}
                                              fetchOrders={fetchCustomerOrder}
                                              fetchOrdersByFilter={fetchCustomerOrder}
                                              data={{customerId: params.customerId}} columns={{md: 4}}/>
                            </div>
                        </div>
                    </Card>
                    <form>
                        <div className="d-flex justify-content-between mb-7">
                            <div>
                                <input type="checkbox"
                                       className="form-check-input me-2"
                                       checked={isAllSelected}
                                       onChange={handleSelectAll}
                                />
                                <label className="fs-md fw-medium">Select all</label>
                            </div>
                            {totalSelectedPrice > 0 && (
                                <div className="d-flex">
                                    <select
                                        className="form-select form-select-sm me-3"
                                        style={{maxWidth: 250}}
                                        name="paymentMethod"
                                        value={paymentMethod}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                    >
                                        <option value="" disabled>--Select payment method</option>
                                        {
                                            Object.keys(PaymentType).map((key) => {
                                                const value = PaymentType[key];
                                                return <option key={key} value={value}>{camelToTitle(key)}</option>
                                            })
                                        }
                                    </select>
                                    <Button className="btn-md bg-primary bg-opacity-50 text-nowrap"
                                            onClick={handleSettleSelectedDues}>
                                        Pay <span
                                        className="fs-sm">&#8377; </span>{totalSelectedPrice.toFixed(2)}<span>/-</span>
                                    </Button>

                                    <ConfirmModal
                                        isOpen={modalOpen}
                                        onCancel={() => setModalOpen(false)}
                                        onConfirm={confirmHandler}
                                    />
                                </div>
                            )}
                        </div>
                        <table className="table table-sm fs-md mobile-responsive">
                            <thead>
                            <tr>
                                <th></th>
                                <th>Order no.</th>
                                <th>To pay</th>
                                <th>Payment type</th>
                                <th>Payment Status</th>
                                <th>Order Status</th>
                                <th>Order Time</th>
                            </tr>
                            </thead>
                            <tbody>
                            {items.map((item) => (
                                <tr key={item._id}>
                                    <td>
                                        <input type="checkbox"
                                               className="form-check-input"
                                               checked={
                                                   item.paymentStatus !== PaymentStatus.Paid &&
                                                   item.paymentStatus !== PaymentStatus.Failed &&
                                                   selectedOrders.some(order => order.orderId === item._id)
                                               }
                                               disabled={
                                                   item.paymentStatus === PaymentStatus.Paid ||
                                                   item.paymentStatus === PaymentStatus.Failed
                                               }
                                               onChange={(e) => handleSelectedOrders(e, item._id, item?.finalPrice)}/>
                                    </td>
                                    <td data-label="Order no.">{item.orderNo}</td>
                                    <td data-label="To pay">{item.finalPrice.toFixed(2)}</td>
                                    <td data-label="Payment type">{item.paymentType}</td>
                                    <td data-label="Payment Status">
                                        {reverseEnum(PaymentStatus)[item.paymentStatus]}
                                    </td>
                                    <td data-label="Order Status">
                                        {reverseEnum(OrderStatus)[item.orderStatus]}
                                    </td>
                                    <td data-label="Order Time">
                                        {moment(item.orderTime).tz(timezone).format('ddd, MMM DD, YYYY, hh:mm A')}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                        {items.length <= 0 && <div>No record to display.</div>}
                        {error && <div>{error}</div>}
                    </form>
                </>
            }

        </>
    );
}
export default CustomerOrder;